import { ModalNormal, CustomSelect, CustomInput, RadioGroup } from '@/shared/ui/components';
import { t } from 'ttag';
import { useSendFeedbackModal } from './useSendFeedbackModal';
import type { SendFeedbackModalProps } from './sendFeedbackModal.types';
import './sendFeedbackModal.scss';

/**
 * Renders the support feedback modal with a category radio group, an email field and a message textarea bound to the useSendFeedbackModal state. The facility select only appears when the user has more than one support facility, email and message surface their validation errors, submitting sends the feedback while the modal shows the saving state, and the cancel button is hidden in favor of the close handler.
 *
 * @param open - Whether the feedback modal is visible.
 * @param toggleSendFeedbackModal - Callback closing the modal, also passed to the hook.
 * @returns The send feedback modal.
 */
const SendFeedbackModal = ({ open, toggleSendFeedbackModal }: SendFeedbackModalProps) => {
  const { categoryList, state, onChange, onChangeFacility, onClickSendMessage } =
    useSendFeedbackModal({ toggleSendFeedbackModal });

  return (
    <ModalNormal
      visible={open}
      title={t`Send feedback`}
      className="send-feedback-modal"
      handleSubmit={onClickSendMessage}
      handleCancel={toggleSendFeedbackModal}
      hideCancelButton
      loading={state.isSaving}
    >
      <div className="send-feedback-box">
        <div className="category-box">
          <span className="title-feedback">{t`Select a category`}</span>
          <div className="input-margin">
            <RadioGroup
              name="category"
              onChange={onChange}
              options={categoryList.current}
              value={state.category}
              size="small"
            />
          </div>
        </div>
        {state.formattedSupportFacilities.length > 1 && (
          <div className="your-facility">
            <span className="title-feedback">{t`Your facility`}</span>
            <div className="input-margin">
              <CustomSelect
                className="custom-your-facilities"
                name="selectedFacility"
                options={state.formattedSupportFacilities}
                value={state.selectedFacility}
                onChange={onChangeFacility}
                allowClear={false}
                size="middle"
                aria-label={t`Your facility`}
              />
            </div>
          </div>
        )}
        <div className="your-email-box">
          <span className="title-feedback">{t`Your email`}</span>
          <div className="input-margin">
            <CustomInput
              onChange={onChange}
              placeholder={t`Enter your email`}
              value={state.replyEmail}
              name="replyEmail"
              isError={!!state.errorObject.replyEmail}
              errorMessage={state.errorObject.replyEmail}
              size="middle"
            />
          </div>
        </div>
        <div className="message-box">
          <span className="title-feedback">{t`Your message`}</span>
          <div className="input-margin">
            <CustomInput
              className="message-input"
              name="content"
              placeholder={t`Enter your message`}
              value={state.content}
              onChange={onChange}
              isError={!!state.errorObject.content}
              errorMessage={state.errorObject.content}
              type="textarea"
            />
          </div>
        </div>
      </div>
    </ModalNormal>
  );
};

export default SendFeedbackModal;
